import { useState } from 'react';
import { Users, Loader2, FolderInput } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useProject } from '../../contexts/ProjectContext';
import { useCopy, fill } from '../../i18n';
import { plural } from '../../i18n/format';
import { teamService } from '../../services/teamService';
import { toast } from 'sonner';
import type { ProjectMemberRole } from '../../types';

interface OrgProjectShareModalProps {
  projectId: string;
  projectName: string;
  onClose: () => void;
}

export default function OrgProjectShareModal({ projectId, projectName, onClose }: OrgProjectShareModalProps) {
  const { currentOrg, organizations, moveProjectToOrg, shareProjectWithOrg, orgMembers } = useOrganization();
  const { loadProjects } = useProject();
  const t = useCopy().account.org.projectShare;
  const [selectedOrgId, setSelectedOrgId] = useState(currentOrg?.id || '');
  const [defaultRole, setDefaultRole] = useState<ProjectMemberRole>('editor');
  const [shareWithMembers, setShareWithMembers] = useState(true);
  const [addAsResources, setAddAsResources] = useState(false);
  const [weeklyHours, setWeeklyHours] = useState('40');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedOrgId) return;

    setSaving(true);
    try {
      // Mover projeto para a org
      await moveProjectToOrg(projectId, selectedOrgId);

      // Compartilhar com todos os membros se solicitado
      let sharedCount = 0;
      if (shareWithMembers) {
        sharedCount = await shareProjectWithOrg(projectId, defaultRole);

        // Adicionar membros como recursos do projeto
        if (addAsResources && orgMembers.length > 0) {
          try {
            // Ler o time DEPOIS do share: compartilhar já espelha cada
            // project_member em team_member (trigger trg_espelhar_membro_como_pessoa),
            // então criar a lista inteira aqui duplicaria todo mundo. Mesma
            // filtragem do TeamImportPanel: por vínculo e por e-mail.
            const jaNoTime = await teamService.getByProject(projectId).catch(() => []);
            const idsLigados = new Set(jaNoTime.filter(m => m.linked_user_id).map(m => m.linked_user_id));
            const emailsNoTime = new Set(
              jaNoTime.filter(m => m.email).map(m => m.email!.toLowerCase())
            );

            const faltantes = orgMembers.filter(member => {
              if (!member.user) return false;
              if (idsLigados.has(member.user_id)) return false;
              return !emailsNoTime.has(member.user.email.toLowerCase());
            });

            if (faltantes.length > 0) {
              await teamService.createMany(
                faltantes.map(member => ({
                  project_id: projectId,
                  name: member.user!.full_name || member.user!.email,
                  email: member.user!.email,
                }))
              );
            }
          } catch {
            // Silently ignore — resource creation is optional
          }
        }
      }

      // Refresh sidebar para refletir a mudança imediatamente
      await loadProjects();

      toast.success(
        sharedCount > 0
          ? fill(plural(sharedCount, t.movedAndShared), { count: sharedCount })
          : t.moved
      );
      onClose();
    } catch (err) {
      toast.error(t.moveError);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader className="flex-row items-center gap-3 space-y-0 pb-1">
          <div className="w-10 h-10 rounded-full bg-[var(--color-primary)]/10 flex items-center justify-center shrink-0">
            <FolderInput size={20} className="text-[var(--color-primary)]" />
          </div>
          <div className="flex flex-col min-w-0">
            <DialogTitle className="text-base leading-tight">{t.title}</DialogTitle>
            <DialogDescription className="truncate max-w-[240px] mt-0.5">
              {projectName}
            </DialogDescription>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4 mt-2">
          <div className="flex flex-col gap-1.5">
            <Label>{t.orgLabel}</Label>
            <Select value={selectedOrgId} onValueChange={setSelectedOrgId}>
              <SelectTrigger>
                <SelectValue placeholder={t.orgPlaceholder} />
              </SelectTrigger>
              <SelectContent>
                {organizations.map(org => (
                  <SelectItem key={org.id} value={org.id}>
                    {fill(t.orgOption, {
                      name: org.name,
                      members: fill(plural(org.memberCount ?? 0, t.memberCount), { count: org.memberCount ?? 0 }),
                    })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="share-with-members"
              checked={shareWithMembers}
              onChange={(e) => setShareWithMembers(e.target.checked)}
              className="rounded"
            />
            <Label htmlFor="share-with-members" className="text-sm cursor-pointer">
              {t.shareWithMembers}
            </Label>
          </div>

          {shareWithMembers && (
            <>
              <div className="flex flex-col gap-1.5 pl-6">
                <Label>{t.defaultRoleLabel}</Label>
                <Select value={defaultRole} onValueChange={(v) => setDefaultRole(v as ProjectMemberRole)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {/* O `value` vai para `project_members.role`; só o rótulo
                        vem do dicionário, indexado por esse mesmo valor. */}
                    <SelectItem value="viewer">{t.roleOptions.viewer}</SelectItem>
                    <SelectItem value="editor">{t.roleOptions.editor}</SelectItem>
                    <SelectItem value="admin">{t.roleOptions.admin}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2 pl-6">
                <input
                  type="checkbox"
                  id="add-as-resources"
                  checked={addAsResources}
                  onChange={(e) => setAddAsResources(e.target.checked)}
                  className="rounded"
                />
                <Label htmlFor="add-as-resources" className="text-sm cursor-pointer">
                  {t.addAsResources}
                </Label>
              </div>

              {addAsResources && (
                <div className="flex flex-col gap-1.5 pl-12">
                  <Label>{t.weeklyHours}</Label>
                  <Input
                    type="number"
                    min="1"
                    max="168"
                    value={weeklyHours}
                    onChange={(e) => setWeeklyHours(e.target.value)}
                    className="w-24"
                  />
                </div>
              )}
            </>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>
              {t.cancel}
            </Button>
            <Button type="submit" variant="primary" disabled={saving || !selectedOrgId}>
              {saving ? (
                <>
                  <Loader2 size={14} className="animate-spin mr-1" />
                  {t.moving}
                </>
              ) : (
                t.submit
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
